import { useEffect, useState } from "react";
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useSelector } from "react-redux";
import keycloak from "../../keycloak";
import { IStore } from "../../store/rootReducer";
import API from "../../api/Api";

interface UserInfo {
  initials: string;
  username: string;
}

interface UserAvatarProps {
  onLogout?: () => void;
}

interface KeycloakUserClaims {
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
}

const getUserInfo = (): UserInfo | null => {
  const claims = keycloak.tokenParsed as KeycloakUserClaims | undefined;
  if (!claims) return null;

  const givenName = claims.given_name || "";
  const familyName = claims.family_name || "";
  const preferredUsername = claims.preferred_username || "";
  const initials = (givenName.charAt(0) + familyName.charAt(0)).toUpperCase();

  return {
    initials,
    username: preferredUsername,
  };
};

export const UserAvatar = ({ onLogout }: UserAvatarProps = {}) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<boolean>(false);

  // Listen to avatar updates from Redux store
  const avatarUpdated = useSelector((state: IStore) => state.customer.avatarUpdated);

  useEffect(() => {
    setUserInfo(getUserInfo());
    loadUserAvatar();
  }, [avatarUpdated]); // Re-load avatar when avatarUpdated changes

  const loadUserAvatar = async () => {
    try {
      const avatarLink = await API.getCustomerAvatar();
      setAvatarUrl(avatarLink);
      setAvatarError(false);
    } catch (error) {
      setAvatarError(true);
      setAvatarUrl(null);
    }
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    if (onLogout) {
      onLogout();
    } else {
      keycloak.logout();
    }
  };

  if (!userInfo) return null;

  return (
    <Dropdown isOpen={isDropdownOpen} toggle={() => setIsDropdownOpen(!isDropdownOpen)} direction="down">
      <DropdownToggle tag="div" className="d-flex align-items-center me-3" style={{ cursor: "pointer" }}>
        <div
          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center position-relative"
          style={{
            width: "40px",
            height: "40px",
            fontSize: "16px",
            fontWeight: "bold",
            backgroundImage: avatarUrl && !avatarError ? `url(${avatarUrl})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {!avatarUrl || avatarError ? userInfo?.initials ? userInfo.initials : <i className="bi-person"></i> : null}
        </div>
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem className="text-dark text-decoration-none small fw-bold" style={{ cursor: "default" }}>
          {userInfo?.username}
        </DropdownItem>
        <DropdownItem onClick={handleLogout} className="text-dark text-decoration-none medium">
          <i className="bi-box-arrow-right me-2"></i>Logout
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};
